import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiPlus,
    FiEdit2,
    FiTrash2,
    FiEye,
    FiAlertTriangle,
    FiClock,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import timetableService from "../../services/timetableService";
import ConfirmModal from "../../components/common/ConfirmModal";
import courseService from "../../services/courseService";
import { getYearOptions, getSemesterOptions } from "../../utils/courseYears";


// colour per course
const COURSE_COLORS = {
    "D. PHARM": "#003b67",
    "B. PHARM": "#059669",
    "M. PHARM": "#7c3aed",
    "D. Pharm": "#003b67",
    "B. Pharm": "#059669",
    "M. Pharm": "#7c3aed",
};

const normalizeTimetableList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

export default function TimetableList() {
    const navigate = useNavigate();
    const { academicYear, isLoggedIn } = useApp();
    const [timetables, setTimetables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [courseFilter, setCourseFilter] = useState("");
    const [courses, setCourses] = useState([]);

    useEffect(() => {
        courseService.getAll({ isActive: true })
            .then((res) => setCourses(res.data || []))
            .catch((err) => toast.error("Couldn't load courses: " + err.message));
    }, []);

    const fetchTimetables = useCallback(async () => {
        setLoading(true);
        try {
            const res = await timetableService.getAll({
                ...(academicYear?._id && {
                    academicYear: academicYear._id,
                }),
                ...(courseFilter && {
                    course: courseFilter,
                }),
            });
            setTimetables(normalizeTimetableList(res));
        } catch (err) {
            setTimetables([]);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [academicYear, courseFilter]);

    useEffect(() => {
        fetchTimetables();
    }, [fetchTimetables]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await timetableService.remove(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchTimetables();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    // Which course+year+semester combos don't have a timetable yet
    const existing = new Set(
        timetables.map((t) => {
            const courseId =
                typeof t.course === "object"
                    ? t.course?._id
                    : t.course;

            return `${courseId}||${t.yearNumber}||${t.semesterNumber || ""}`;
        })
    );

    const missing = [];

    courses.forEach((course) => {
        const semOptions = getSemesterOptions(course);
        if (semOptions.length > 0) {
            semOptions.forEach((s) => {
                const key = `${course._id}||${s.yearNumber}||${s.semesterNumber}`;
                if (!existing.has(key)) {
                    missing.push({
                        courseId: course._id,
                        courseName: course.shortName,
                        yearNumber: s.yearNumber,
                        yearLabel: `${s.yearNumber}${s.yearNumber === 1 ? "st" : s.yearNumber === 2 ? "nd" : s.yearNumber === 3 ? "rd" : "th"} Year`,
                        semesterNumber: s.semesterNumber,
                        semesterLabel: s.label,
                    });
                }
            });
        } else {
            getYearOptions(course).forEach((y) => {
                const key = `${course._id}||${y.yearNumber}||`;
                if (!existing.has(key)) {
                    missing.push({
                        courseId: course._id,
                        courseName: course.shortName,
                        yearNumber: y.yearNumber,
                        yearLabel: y.label,
                        semesterNumber: null,
                        semesterLabel: "",
                    });
                }
            });
        }
    });

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">Timetables</h1>
                    <p className="page-subtitle">
                        {academicYear
                            ? `${timetables.length} timetable${timetables.length !== 1 ? "s" : ""} — ${academicYear.label}`
                            : `${timetables.length} public timetable${timetables.length !== 1 ? "s" : ""}`}
                    </p>
                </div>
                {isLoggedIn && (
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/timetables/new")}
                        disabled={!academicYear}
                    >
                        <FiPlus size={14} /> Create Timetable
                    </button>
                )}
            </div>

            {/* ── No year warning ── */}
            {isLoggedIn && !academicYear && (
                <div
                    style={{
                        background: "#fef3c7",
                        border: "1px solid #fcd34d",
                        borderRadius: 8,
                        padding: "0.75rem 1rem",
                        marginBottom: "1rem",
                        fontSize: 13,
                        color: "#92400e",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <FiAlertTriangle size={14} /> Select an academic year from the
                    Dashboard to view timetables.
                </div>
            )}

            <>
                {/* ── Missing timetables notice ── */}
                {academicYear && missing.length > 0 && (
                    <div
                        style={{
                            background: "#e8f1f8",
                            border: "1px solid #b6d5ec",
                            borderRadius: 8,
                            padding: "0.85rem 1rem",
                            marginBottom: "1rem",
                            fontSize: 13,
                            color: "#002747",
                        }}
                    >
                        <strong>{missing.length}</strong> course–semester combination
                        {missing.length !== 1 ? "s" : ""} have no timetable yet:&nbsp;
                        {missing.slice(0, 4).map((m, i) => (
                            <span key={i}>
                                <button
                                    style={{
                                        background: "none",
                                        border: "none",
                                        color: "#003b67",
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        fontSize: 13,
                                        padding: 0,
                                    }}
                                    onClick={() =>
                                        navigate(
                                            `/timetables/new?course=${encodeURIComponent(
                                                m.courseId
                                            )}&year=${encodeURIComponent(
                                                m.yearNumber
                                            )}${
                                                m.semesterNumber
                                                    ? `&semesterNumber=${encodeURIComponent(
                                                          m.semesterNumber
                                                      )}`
                                                    : ""
                                            }`
                                        )
                                    }
                                >
                                    {m.courseName} {m.yearLabel}{m.semesterLabel ? ` · ${m.semesterLabel}` : ""}
                                </button>

                                {i < Math.min(missing.length, 4) - 1 ? ", " : ""}
                            </span>
                        ))}
                    </div>
                )}

                <div className="card">
                    {/* ── Filter ── */}
                    <div
                        style={{
                            padding: "0.75rem 1rem",
                            borderBottom: "1px solid var(--border)",
                            display: "flex",
                            gap: "0.75rem",
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <select
                            className="form-control"
                            style={{ width: 180, fontSize: 13 }}
                            value={courseFilter}
                            onChange={(e) => setCourseFilter(e.target.value)}
                        >
                            <option value="">All Courses</option>
                            {courses.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.shortName}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* ── Content ── */}
                    {loading ? (
                        <div className="loading-center">
                            <div className="spinner" />
                            Loading timetables...
                        </div>
                    ) : timetables.length === 0 ? (
                        <div className="empty-state">
                            <FiClock
                                size={48}
                                style={{
                                    opacity: 0.25,
                                    display: "block",
                                    margin: "0 auto 0.75rem",
                                }}
                            />
                            <p>
                                {isLoggedIn
                                    ? "No timetables found. Create one to get started."
                                    : "No timetables found."}
                            </p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Course</th>
                                        <th>Year & Sem</th>
                                        <th style={{ textAlign: "center" }}>Period Slots</th>
                                        <th>Effective From</th>
                                        <th>Notes</th>
                                        <th style={{ textAlign: "right" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {timetables.map((t, i) => {
                                        const courseName =
                                            typeof t.course === "object"
                                                ? t.course?.shortName
                                                : "";

                                        const yearLabel =
                                            typeof t.course === "object"
                                                ? getYearOptions(t.course).find(
                                                    (y) =>
                                                        Number(y.yearNumber) ===
                                                        Number(t.yearNumber)
                                                )?.label
                                                : `Year ${t.yearNumber}`;

                                        const color =
                                            COURSE_COLORS[courseName] || "#6b7280";

                                        return (
                                            <tr key={t._id}>
                                                <td
                                                    style={{
                                                        fontSize: 12,
                                                        color: "var(--text-muted)",
                                                    }}
                                                >
                                                    {i + 1}
                                                </td>

                                                <td>
                                                    <span
                                                        style={{
                                                            background: `${color}18`,
                                                            color,
                                                            padding: "3px 10px",
                                                            borderRadius: 99,
                                                            fontSize: 12.5,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {courseName || "—"}
                                                    </span>
                                                </td>

                                                <td style={{ fontWeight: 600 }}>
                                                    <div>{yearLabel || "—"}</div>
                                                    {t.semesterNumber && (
                                                        <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 500 }}>
                                                            Semester {t.semesterNumber}
                                                        </span>
                                                    )}
                                                </td>

                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                        fontSize: 13,
                                                    }}
                                                >
                                                    {t.periodSlots?.length ?? 0} slot
                                                    {t.periodSlots?.length !== 1 ? "s" : ""}
                                                </td>

                                                <td style={{ fontSize: 13 }}>
                                                    {t.effectiveFrom ? (
                                                        <span className="badge badge-blue">
                                                            {t.effectiveFrom}
                                                        </span>
                                                    ) : (
                                                        <span
                                                            style={{
                                                                color: "var(--text-muted)",
                                                            }}
                                                        >
                                                            —
                                                        </span>
                                                    )}
                                                </td>

                                                <td
                                                    style={{
                                                        fontSize: 12.5,
                                                        color: "var(--text-muted)",
                                                        maxWidth: 200,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {t.notes || "—"}
                                                    </div>
                                                </td>

                                                <td>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            gap: "0.35rem",
                                                            justifyContent: "flex-end",
                                                        }}
                                                    >
                                                        <button
                                                            className="btn btn-outline btn-sm"
                                                            title="View timetable"
                                                            onClick={() => navigate(`/timetables/${t._id}`)}
                                                        >
                                                            <FiEye size={12} /> View
                                                        </button>
                                                        {isLoggedIn && (
                                                            <>
                                                                <button
                                                                    className="btn btn-outline btn-sm"
                                                                    title="Edit timetable"
                                                                    onClick={() =>
                                                                        navigate(`/timetables/${t._id}/edit`)
                                                                    }
                                                                >
                                                                    <FiEdit2 size={12} />
                                                                </button>
                                                                <button
                                                                    className="btn btn-danger btn-sm"
                                                                    title="Delete timetable"
                                                                    onClick={() => setDeleteTarget(t)}
                                                                >
                                                                    <FiTrash2 size={12} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Timetable"
                    message={`Delete the timetable for "${deleteTarget.course?.shortName || "Unknown Course"
                        } — ${deleteTarget.yearLabel ||
                        `Year ${deleteTarget.yearNumber}`
                        }"? This cannot be undone.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
